// The "Magasins" card — assign an org's shops to a user or a team.
//
// Shared by the user-detail page and the team-composition drawer. It:
//   • loads the org's shop CATALOG (proxied from solya-app via /org-shops),
//   • loads the CURRENT selection (the target's direct user_shop / team_shop ids),
//   • lets the admin toggle shops and SAVE — a full-replacement PUT.
//
// VIEW_ALL guard: when the target holds a role granting `shops.viewAll` it already
// sees every shop, so per-shop assignment is moot. The picker is then replaced by a
// banner naming the granting role (ported from solya-app's UserShopAccessPanel).
//
// The `roleIds` prop is the set of roles that apply to the target (a user's assigned
// roles, or a team's granted roles); their details are fetched to detect VIEW_ALL.

import { useEffect, useMemo, useState } from "react"
import { adminApi } from "../api/endpoints.js"
import type { OrgShopDto, RoleDetailDto } from "../api/types.js"
import { useWritesEnabled } from "../auth/AdminAuthContext.js"
import { fr } from "../i18n/fr.js"
import { messageForError } from "../lib/errors.js"
import { collectRolesGrantingViewAll, roleLabel } from "../lib/shops.js"
import { useAsync } from "../lib/useAsync.js"
import { Alert, Button, Card, Spinner, ToggleChip } from "../ui/primitives.js"
import { useNotify } from "../ui/toast.js"

interface ShopAccessCardProps {
  /** The active org (drives the catalog fetch). */
  org: string
  /** The roles that apply to the target — fetched to detect a VIEW_ALL grant. */
  roleIds: string[]
  /** Hint copy under the title (user vs team). */
  hint: string
  /** Load the target's current shop ids (listUserShops / listTeamShops). */
  loadShops: () => Promise<string[]>
  /** Persist the full replacement (setUserShops / setTeamShops). */
  saveShops: (shopIds: string[]) => Promise<unknown>
  /** Changes when the target changes → refetch the current selection. */
  reloadKey: string
}

export function ShopAccessCard({
  org,
  roleIds,
  hint,
  loadShops,
  saveShops,
  reloadKey
}: ShopAccessCardProps) {
  const notify = useNotify()
  const writesEnabled = useWritesEnabled()

  const catalog = useAsync<OrgShopDto[]>(() => adminApi.listOrgShops(), [org])
  const current = useAsync<string[]>(loadShops, [reloadKey])

  // Resolve the applicable roles' details to detect a VIEW_ALL grant. Same
  // Promise.all(getRole) shape the user-detail page already uses for permissions.
  const roleKey = [...roleIds].sort().join(",")
  const roleDetails = useAsync<RoleDetailDto[]>(
    () =>
      roleIds.length > 0
        ? Promise.all(roleIds.map((id) => adminApi.getRole(id)))
        : Promise.resolve([]),
    [roleKey]
  )
  const viewAllRoles = useMemo(
    () => collectRolesGrantingViewAll(roleDetails.data ?? []),
    [roleDetails.data]
  )
  const hasViewAll = viewAllRoles.length > 0

  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  // Seed the staged selection from the loaded current set (and on target change).
  useEffect(() => {
    if (current.data) setSelected(new Set(current.data))
  }, [current.data])

  const dirty = useMemo(() => {
    const base = new Set(current.data ?? [])
    if (base.size !== selected.size) return true
    for (const id of selected) if (!base.has(id)) return true
    return false
  }, [current.data, selected])

  function toggle(shopId: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(shopId)) next.delete(shopId)
      else next.add(shopId)
      return next
    })
  }

  async function save() {
    if (!writesEnabled || hasViewAll) return
    setSaving(true)
    try {
      await saveShops([...selected].sort())
      notify.success(fr.shops.saved)
      current.reload()
    } catch (err) {
      notify.error(messageForError(err, fr.shops.saveError))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card>
      <div className="card__header">
        <strong>{fr.shops.title}</strong>
        {!hasViewAll && current.data && !current.loading ? (
          <span className="muted" style={{ fontSize: 13 }}>
            {fr.shops.count(selected.size)}
          </span>
        ) : null}
      </div>
      <p className="muted" style={{ margin: "4px 0 0" }}>
        {hint}
      </p>

      {hasViewAll ? (
        <Alert variant="info">{fr.shops.viewAllBanner(roleLabel(viewAllRoles[0]))}</Alert>
      ) : (
        <>
          {catalog.loading || current.loading || roleDetails.loading ? (
            <Spinner label={fr.shops.loading} />
          ) : null}
          {catalog.error ? <Alert variant="error">{fr.shops.catalogError}</Alert> : null}
          {current.error ? <Alert variant="error">{fr.shops.error}</Alert> : null}
          {catalog.data && catalog.data.length === 0 && !catalog.loading ? (
            <p className="muted" style={{ margin: "8px 0 0" }}>
              {fr.shops.catalogEmpty}
            </p>
          ) : null}
          {catalog.data && catalog.data.length > 0 ? (
            <>
              <div className="row" style={{ gap: 6, marginTop: 12 }}>
                {catalog.data.map((shop) => (
                  <ToggleChip
                    key={shop.id}
                    selected={selected.has(shop.id)}
                    onToggle={() => toggle(shop.id)}
                    title={fr.shops.toggleLabel(shop.name)}
                  >
                    {shop.name}
                    {shop.code ? ` (${shop.code})` : ""}
                  </ToggleChip>
                ))}
              </div>
              <div style={{ marginTop: 12 }}>
                <Button
                  variant="primary"
                  disabled={!writesEnabled || saving || !dirty}
                  onClick={save}
                >
                  {fr.shops.save}
                </Button>
              </div>
            </>
          ) : null}
        </>
      )}
    </Card>
  )
}
